import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ToastProvider } from "react-toast-notifications";
import Login from "./components/Auth/Login";
import RequireAuth from "./components/Auth/RequireAuth";
import { AuthProvider } from "./components/Auth/Auth";
import OrderStatusPage from "./components/Pages/Order";
import Redirect from "./components/Pages/Redirect";
import BulkOrderStatusPage from "./components/Pages/bulk-order-status";

/**
 * Every route uses the same Bootstrap build now that the Argon-themed /status
 * variant is gone, so there is one stylesheet and nothing to choose between.
 */
const loadStyles = () => import("./assets/scss/main.scss");

/** Sends a stray /status link to the order page, credentials and all. */
export const RedirectToOrderPage = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/${search}${hash}`} replace />;
};

function App() {
  const [stylesReady, setStylesReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Gate the first paint on the stylesheet actually resolving, rather than
    // on a fixed 1s setTimeout that delayed every visitor.
    loadStyles()
      .catch((error) => console.error("Failed to load stylesheets:", error))
      .finally(() => {
        if (!cancelled) setStylesReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ToastProvider>
      <AuthProvider>
        {stylesReady ? (
          <Routes>
            <Route
              path="/"
              element={
                <RequireAuth>
                  <OrderStatusPage />
                </RequireAuth>
              }
            />
            {/*
              /status served an argon-themed duplicate of the order page. It
              was added in Aug 2023, never linked from anywhere - not this app,
              not the backend, which has never built a /status URL in its
              history, and not any sibling repo - and took no organic traffic.
              Keeping it meant every change to the order actions had to be made
              twice, and the one time it was not, the two pages disagreed about
              whether a customer could cancel.

              Redirect rather than 404 so any bookmark or hand-shared link
              still lands on the order it names.
            */}
            <Route path="/status" element={<RedirectToOrderPage />} />
            <Route path="/bulk-order" element={<BulkOrderStatusPage />} />
            <Route path="/r/:id" element={<Redirect />} />
            <Route path="/login" element={<Login />} />
          </Routes>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "100vh" }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
